import React from 'react';
import { PhoneSearchRow, PhoneSearchToggle, usePhoneSearch } from './PhoneSearch';
import { PHONE_FULL_BLEED } from '../constants/phoneLayout';

/**
 * THE phone (below `md`) page header — ONE shape on every list page (owner
 * ruling 2026-10-03: "fix the header so it's not three shapes. Leave search on
 * row 2 as standard unless there's only one row, then it's on the first row").
 * Extracted 2026-10-01 from `pages/MyOrders.js` as the magnifier + tab row;
 * grown 2026-10-03 into the two-row standard. Pages render THIS, never a copy.
 *
 *   Row 1  [ tabs ………………………………………………… ]          ← `tabStrip` only
 *   Row 2  [🔍]   [View ▾]   [+]   [Actions ▾]      ← `justify-between`
 *   (search row, under the last row, while open)
 *
 * - Any of `view` / `add` / `actions` / `note` given → the TWO-row shape
 *   (row 1 is omitted when there is no `tabStrip` — To Approve).
 * - None given → the ONE-row shape: 🔍 + tabs (`${idPrefix}-mobile-tab-row`).
 * - `note` (To Approve only): muted text that fills row 2 after the 🔍.
 * The page renders it INSIDE its `ui/Toolbar`, so every row pins; put
 * `PHONE_HEADER_TOOLBAR` on that Toolbar's `className` (2px padding, 2px under
 * the bar, edge to edge). Slots render no node when absent.
 *
 * 🔑 (The search toggle + row live in `PhoneSearch.js` since 2026-10-28, shared with the
 * timesheets phone header.) The search row is open while the user opened it OR while a term is
 * active, so a live filter can never sit hidden behind a closed row (the
 * "narrowed list looks like a short list" trap `OrderSearchAndSort` warns
 * about). ✕ clears AND closes. The magnifier: closed → open + focus; open with
 * a term → refocus; open and empty → close.
 *
 * The page keeps the search STATE (`searchText`/`onSearchChange`) — it filters
 * by it. Testids: `${idPrefix}-phone-header` (wrapper), `-mobile-tab-row`
 * (row 1, or the single row), `-phone-controls` (row 2), `-search-toggle`,
 * `-mobile-search-row`, `-search-close`; the input is `OrderSearchAndSort`'s
 * own, under `searchTestIdPrefix`. 🔻 `trailing` (Actions on the tab row) was
 * DELETED 2026-10-03 — Actions is a row-2 control now.
 */
export const PHONE_HEADER_TOOLBAR = `max-md:py-0.5 max-md:mb-0.5 ${PHONE_FULL_BLEED}`;

export default function MobileTabSearchRow({
  idPrefix,
  searchTestIdPrefix,
  searchText,
  onSearchChange,
  searchPlaceholder,
  searchLabel = 'Search orders',
  tabStrip = null,
  view = null,
  add = null,
  actions = null,
  note = null,
  // 📱 2026-10-02, the Gag Breakdown: its search REFETCHES `/api/gags`, so it
  // passes `300` and names the box. Both default to the order pages' values —
  // see `OrderSearchAndSort`'s `debounceMs`.
  debounceMs,
  searchAriaLabel,
}) {
  const search = usePhoneSearch({ searchText, onSearchChange });
  const twoRow = Boolean(view || add || actions || note);
  const searchToggle = <PhoneSearchToggle idPrefix={idPrefix} search={search} label={searchLabel} />;
  return (
    <div className="w-full min-w-0 flex flex-col gap-0.5 px-1" data-testid={`${idPrefix}-phone-header`}>
      {twoRow ? (
        <>
          {tabStrip ? (
            <div className="flex items-end w-full min-w-0" data-testid={`${idPrefix}-mobile-tab-row`}>
              {tabStrip}
            </div>
          ) : null}
          <div className="flex items-center justify-between w-full min-w-0 gap-0.5" data-testid={`${idPrefix}-phone-controls`}>
            {searchToggle}
            {note}
            {/* The View chip is the one that gives way — it ellipses (its menu
                still names the choice); measured at 375 on My Tasks. */}
            {view ? <div className="min-w-0 [&>div]:min-w-0">{view}</div> : null}
            {add}
            {actions ? <div className="shrink-0 flex items-center gap-0.5">{actions}</div> : null}
          </div>
        </>
      ) : (
        /* Magnifier + tabs on ONE row; the strip starts flush left right
           after the icon (`ui/Tabs`' `snapFlushStart`). */
        <div className="flex items-end w-full min-w-0 gap-1" data-testid={`${idPrefix}-mobile-tab-row`}>
          {searchToggle}
          {tabStrip}
        </div>
      )}
      <PhoneSearchRow idPrefix={idPrefix} search={search} searchText={searchText} onSearchChange={onSearchChange}
        searchPlaceholder={searchPlaceholder} searchAriaLabel={searchAriaLabel} debounceMs={debounceMs} searchTestIdPrefix={searchTestIdPrefix} />
    </div>
  );
}
